import React, { useEffect, useState } from 'react';
import {
    ActionIcon,
    Button,
    Flex,
    Highlight,
    Loader,
    Paper,
    ScrollArea,
    Stack,
    Table,
    Text,
    TextInput,
    Title,
} from '@mantine/core';
import { IconArrowRight, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react';
import { inject, observer } from 'mobx-react';
import { theme } from '../../../global/style/theme.tsx';
import { useTranslation } from 'react-i18next';
import { useGetHealthcareFacilities } from '../../../global/entities/healthcareFacility/queries/useGetHealthcareFacilities.tsx';
import { IHealthcareFacility } from '../../../global/entities/healthcareFacility/HealthcareFacility.ts';
import { Loading } from '../../../global/component/organism/Loading/Loading.tsx';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router';
import { useGetHealthcareFacilityById } from '../../../global/entities/healthcareFacility/queries/useGetHealthcareFacilityById.tsx';
import { useElementSize } from '@mantine/hooks';
import { TableContextProvider, Th, useTable } from '../../../global/hooks/useTable.tsx';
import { containsStringIgnoreCase } from '../../HcpDashboard/Table/PatientTable.tsx';
import { useDeleteHcfMutations } from '../../../global/entities/healthcareFacility/mutations/useDeleteHcfMutations.tsx';
import { modals } from '@mantine/modals';

const HcfTable: React.FC = inject()(
    observer(() => {
        const { t } = useTranslation();
        const { data, isLoading, refetch } = useGetHealthcareFacilities();
        const { isLoading: isLoadingGetHealthcareFacilityById } = useGetHealthcareFacilityById();
        const { mutateAsync: deleteHcf } = useDeleteHcfMutations();
        const params = useParams<{ id: string; tabId: string }>();
        const navigate = useNavigate();
        const { ref } = useElementSize();
        const [search, setSearch] = useState('');
        const { setSorting, sortBy, reverseSortDirection, setSortedData, sortedData } = useTable();

        useEffect(() => {
            setSortedData([]);
            if (data?.length) 
                setSortedData(data);
            
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [params.tabId, data]);

        if (isLoading) 
            return <Loading visible={true} loadingMessage={'Loading...'} />;

        const onClickDelete = (questionnaireId: string) => {
            deleteHcf(questionnaireId)
                .then(() => {
                    refetch();
                })
                .catch((error) => {
                    console.error('Erreur lors de la suppression :', error);
                });
        };

        const openModalDelete = (questionnaireId: string) =>
            modals.openConfirmModal({
                title: t('modal.deleteHcfModal.title'),
                children: (
                    <Stack gap={'sm'}>
                        <Text>{t('modal.deleteHcfModal.question')}</Text>
                        <Text fw={'bold'}> {t('modal.deleteHcfModal.details')}</Text>
                    </Stack>
                ),
                labels: { confirm: t('button.delete'), cancel: t('button.cancel') },
                confirmProps: { color: 'red' },
                cancelProps: { variant: 'outline', color: 'gray' },
                onCancel: () => console.log('Suppression annulée'),
                onConfirm: () => {
                    onClickDelete(questionnaireId);
                },
                size: 'lg',
                radius: 'xs',
            });

        return (
            <>
                <Flex mb={'lg'} mt={'lg'} align={'center'} justify={'space-between'}>
                    <Button onClick={() => navigate('/adminDashboard/hcp')} variant="transparent">
                        <Title c="black" order={4}>
                            {t('navbar.hcf')}
                        </Title>
                    </Button>
                    <Button
                        onClick={() => navigate('/adminDashboard/hcf/0')}
                        leftSection={<IconPlus />}
                        styles={{ label: { align: 'center', justifyContent: 'center' } }}
                    >
                        {t('hcf.addNewHCF')}
                    </Button>
                </Flex>
                <Paper w="100%" ref={ref}>
                    <Stack gap={'md'} h={'min-content'}>
                        <TextInput
                            placeholder={t('global.search')}
                            rightSection={<IconSearch />}
                            value={search}
                            color={'var(--mantine-color-default-color)'}
                            width={'100%'}
                            radius={'md'}
                            onChange={(el) => setSearch(el.currentTarget.value)}
                        />
                        <ScrollArea w="100%">
                            <Table verticalSpacing="xs">
                                <Table.Thead>
                                    <Table.Tr>
                                        <Th
                                            sorted={sortBy === 'name'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('name', sortedData)}
                                        >
                                            {t('formHcf.healthFacilityName')}
                                        </Th>
                                        <Th
                                            sorted={sortBy === 'contactName'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('contactName', sortedData)}
                                        >
                                            {t('formGlobal.contactPerson')}
                                        </Th>
                                        <Th
                                            sorted={sortBy === 'contactPhone'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('contactPhone', sortedData)}
                                        >
                                            {t('formGlobal.phoneNumber')}
                                        </Th>
                                        <Th
                                            sorted={sortBy === 'contactEmail'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('contactEmail', sortedData)}
                                        >
                                            {t('formGlobal.email')}
                                        </Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {sortedData?.map((facility: IHealthcareFacility, index: number) => {
                                        const isSearch =
                                            containsStringIgnoreCase(
                                                [
                                                    `${facility.name}`,
                                                    `${facility.contactName}`,
                                                    `${facility.contactPhone}`,
                                                    `${facility.contactEmail}`,
                                                ],
                                                search,
                                            ) || !search;
                                        return (
                                            <Table.Tr
                                                key={facility.id}
                                                hidden={!isSearch}
                                                style={{
                                                    background:
                                                        index % 2 === 1
                                                            ? undefined
                                                            : 'var(--Background-Gray-light, #E9ECEF)',
                                                }}
                                            >
                                                <Table.Td>
                                                    <Highlight
                                                        highlight={search}
                                                        highlightStyles={{
                                                            color: 'var(--mantine-color-gray-9)',
                                                            fontWeight: 700,
                                                            WebkitBackgroundClip: 'text',
                                                        }}
                                                        styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                                                        truncate
                                                        maw={200}
                                                    >
                                                        {facility.name}
                                                    </Highlight>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Highlight
                                                        highlight={search}
                                                        highlightStyles={{
                                                            color: 'var(--mantine-color-gray-9)',
                                                            fontWeight: 700,
                                                            WebkitBackgroundClip: 'text',
                                                        }}
                                                        styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                                                        truncate
                                                    >
                                                        {facility.contactName}
                                                    </Highlight>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Highlight
                                                        highlight={search}
                                                        highlightStyles={{
                                                            color: 'var(--mantine-color-gray-9)',
                                                            fontWeight: 700,
                                                            WebkitBackgroundClip: 'text',
                                                        }}
                                                        styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                                                        truncate
                                                    >
                                                        {facility.contactPhone}
                                                    </Highlight>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Highlight
                                                        highlight={search}
                                                        highlightStyles={{
                                                            color: 'var(--mantine-color-gray-9)',
                                                            fontWeight: 700,
                                                            WebkitBackgroundClip: 'text',
                                                        }}
                                                        styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                                                        truncate
                                                    >
                                                        {facility.contactEmail}
                                                    </Highlight>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Flex align={'center'}>
                                                        <ActionIcon
                                                            tabIndex={-1}
                                                            variant="subtle"
                                                            color="red"
                                                            onClick={() => openModalDelete(facility.id!.toString())}
                                                        >
                                                            <IconTrash size={18} />
                                                        </ActionIcon>
                                                        <ActionIcon variant="transparent">
                                                            {isLoadingGetHealthcareFacilityById ? (
                                                                <Loader
                                                                    size="sm"
                                                                    color={theme.colors?.blue?.toString()}
                                                                />
                                                            ) : (
                                                                <IconArrowRight
                                                                    color={theme.colors?.blue?.toString()}
                                                                    onClick={() => {
                                                                        navigate(`${facility.id}`);
                                                                    }}
                                                                />
                                                            )}
                                                        </ActionIcon>
                                                    </Flex>
                                                </Table.Td>
                                            </Table.Tr>
                                        );
                                    })}
                                </Table.Tbody>
                            </Table>
                        </ScrollArea>
                    </Stack>
                </Paper>
            </>
        );
    }),
);

const WrappedHcfTable: React.FC = () => {
    return (
        <TableContextProvider>
            <HcfTable />
        </TableContextProvider>
    );
};

export default WrappedHcfTable;
